import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Palette, ShieldCheck, Clock3 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPECIALTY_FALLBACK = [
  { code: "packaging_box", label: "Packaging Box" },
  { code: "label", label: "Label Design" },
  { code: "logo", label: "Logo & Branding" },
  { code: "flexo", label: "Flexo Design" },
  { code: "mockup_3d", label: "3D Mockups" },
  { code: "brochure", label: "Brochure / Marketing Collateral" },
  { code: "preflight_dtp", label: "Pre-flight / DTP Correction" },
];

export default function PartnerStudio() {
  const [specialtiesRef, setSpecialtiesRef] = useState(SPECIALTY_FALLBACK);
  const [form, setForm] = useState({
    studio_name: "",
    contact_person: "",
    email: "",
    phone: "",
    gstin: "",
    city: "",
    state: "",
    team_size: "",
    years_experience: "",
    portfolio_url: "",
    about: "",
    specialties: [],
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/design/reference`)
      .then(r => setSpecialtiesRef(r.data?.specialties || SPECIALTY_FALLBACK))
      .catch(() => {});
  }, []);

  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const toggleSpec = (code) => {
    setForm(f => ({
      ...f,
      specialties: f.specialties.includes(code)
        ? f.specialties.filter(c => c !== code)
        : [...f.specialties, code],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {
        ...form,
        years_experience: form.years_experience === "" ? null : Number(form.years_experience),
      };
      await axios.post(`${API}/public/agency-applications`, payload);
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message || "Could not submit. Please try again or email info@goprintpack.com.");
    } finally { setBusy(false); }
  };

  return (
    <div className="marketing">
      <header className="m-nav" data-testid="partner-studio-navbar">
        <Link to="/" className="m-brand"><img src="/logo.png" alt="GoPrintPack" style={{ height: 32 }}/></Link>
        <nav className="m-nav-links">
          <Link to="/">Home</Link>
          <Link to="/contact">Contact</Link>
          <Link to="/partner">Partner with us</Link>
        </nav>
        <div className="m-nav-actions">
          <Link to="/login" className="m-ghost" data-testid="nav-sign-in">Sign in</Link>
          <Link to="/register" className="m-cta" data-testid="nav-launch-workspace">Get started</Link>
        </div>
      </header>

      <section className="contact-page" data-testid="partner-studio-page">
        <div className="contact-hero">
          <p className="eyebrow">PARTNER WITH GOPRINTPACK</p>
          <h1>Join our verified design & creative supplier network.</h1>
          <p className="subhead">We bring vetted brands with locked escrow — you bring the craft. Apply below and our team will get back within two business days to walk you through verification.</p>
        </div>

        {/* Benefits strip */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:16,maxWidth:1080,margin:"0 auto 40px",padding:"0 24px"}} data-testid="partner-studio-benefits">
          {[
            { icon: ShieldCheck, title: "Escrow-protected briefs", body: "Every job starts with the full order value locked in escrow. You get paid when the buyer approves artwork." },
            { icon: Palette, title: "Rate card you control", body: "Publish fixed project prices per specialty + your paid-revision rate, and get hired with a single click from the Supplier Network." },
            { icon: Clock3, title: "Pre-qualified buyers", body: "No chasing leads — we route briefs that match your specialties, tier, and capacity directly to your inbox." },
          ].map((b, i) => (
            <div key={i} style={{background:"#fff",border:"1px solid #E5E7EB",borderRadius:12,padding:"18px 20px",boxShadow:"0 10px 25px -18px rgba(15,23,42,0.3)"}}>
              <b.icon size={22} color="#2563EB" strokeWidth={1.8}/>
              <h4 style={{margin:"10px 0 4px",fontSize:14,fontWeight:700,color:"#111827"}}>{b.title}</h4>
              <p style={{margin:0,fontSize:12,color:"#6B7280",lineHeight:1.5}}>{b.body}</p>
            </div>
          ))}
        </div>

        {sent ? (
          <div data-testid="partner-success" style={{maxWidth:620,margin:"0 auto 60px",padding:"36px 32px",background:"#F0FDF4",border:"1px solid #86EFAC",borderRadius:16,textAlign:"center"}}>
            <CheckCircle2 size={44} color="#059669" strokeWidth={1.6}/>
            <h2 style={{margin:"14px 0 6px",fontFamily:"'Fraunces', serif",fontSize:26,color:"#065F46"}}>Application received</h2>
            <p style={{margin:"0 0 18px",fontSize:14,color:"#065F46",lineHeight:1.55}}>Thanks for your interest in joining the GoPrintPack™ network. Our team will review your portfolio and reach out within <strong>two business days</strong> with the next steps.</p>
            <Link to="/" style={{display:"inline-block",background:"#059669",color:"#fff",textDecoration:"none",padding:"10px 22px",borderRadius:8,fontWeight:600,fontSize:13}}>Back to home</Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="partner-form" style={{maxWidth:860,margin:"0 auto 80px",padding:"28px 32px",background:"#fff",border:"1px solid #E5E7EB",borderRadius:16,boxShadow:"0 25px 60px -40px rgba(15,23,42,0.4)"}}>
            {err && <div style={{background:"#FEF2F2",border:"1px solid #FECACA",color:"#991B1B",padding:"10px 14px",borderRadius:8,fontSize:13,marginBottom:16}} data-testid="partner-error">{err}</div>}

            <h3 style={{margin:"0 0 4px",fontSize:12,letterSpacing:"0.14em",textTransform:"uppercase",color:"#6B7280",fontWeight:700}}>About the studio</h3>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginTop:10}}>
              <PartnerField label="Studio name *" name="studio_name" value={form.studio_name} onChange={set} placeholder="e.g. Studio Field" required maxLength={120}/>
              <PartnerField label="Contact person *" name="contact_person" value={form.contact_person} onChange={set} placeholder="Full name" required maxLength={120}/>
              <PartnerField label="Email *" type="email" name="email" value={form.email} onChange={set} placeholder="studio@example.com" required maxLength={120}/>
              <PartnerField label="Phone *" name="phone" value={form.phone} onChange={set} placeholder="+91 98765 43210" required maxLength={30}/>
              <PartnerField label="GSTIN *" name="gstin" value={form.gstin} onChange={set} placeholder="e.g. 27ABCDE1234F1Z5" required maxLength={20} data-testid="partner-gstin"/>
              <PartnerField label="City" name="city" value={form.city} onChange={set} placeholder="Pune" maxLength={80}/>
              <PartnerField label="State" name="state" value={form.state} onChange={set} placeholder="Maharashtra" maxLength={80}/>
              <PartnerField label="Team size" name="team_size" value={form.team_size} onChange={set} placeholder="e.g. 2-5, 6-15, 16-50" maxLength={40}/>
              <PartnerField label="Years in design" type="number" min={0} max={120} name="years_experience" value={form.years_experience} onChange={set} placeholder="e.g. 7"/>
            </div>
            <PartnerField label="Portfolio URL (Behance / Dribbble / Website)" name="portfolio_url" value={form.portfolio_url} onChange={set} placeholder="https://" maxLength={400} full/>

            <h3 style={{margin:"22px 0 4px",fontSize:12,letterSpacing:"0.14em",textTransform:"uppercase",color:"#6B7280",fontWeight:700}}>What do you do best? *</h3>
            <p style={{margin:"0 0 10px",fontSize:12,color:"#6B7280"}}>Pick every specialty your studio ships regularly. Minimum one.</p>
            <div data-testid="partner-specialties" style={{display:"flex",flexWrap:"wrap",gap:8}}>
              {specialtiesRef.map(s => {
                const on = form.specialties.includes(s.code);
                return (
                  <button
                    key={s.code}
                    type="button"
                    data-testid={`partner-spec-${s.code}`}
                    onClick={() => toggleSpec(s.code)}
                    style={{
                      background: on ? "#111827" : "#F3F4F6",
                      color: on ? "#FACC15" : "#374151",
                      border: `1px solid ${on ? "#111827" : "#D1D5DB"}`,
                      padding: "7px 14px",
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >{s.label}</button>
                );
              })}
            </div>

            <h3 style={{margin:"22px 0 8px",fontSize:12,letterSpacing:"0.14em",textTransform:"uppercase",color:"#6B7280",fontWeight:700}}>Tell us about your studio *</h3>
            <textarea
              data-testid="partner-about"
              name="about"
              value={form.about}
              onChange={set}
              required
              minLength={20}
              maxLength={1200}
              rows={5}
              placeholder="How you work, your most-shipped categories, standout clients or launches, what makes you different… (20–1200 chars)"
              style={{width:"100%",padding:"10px 12px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13,fontFamily:"inherit",resize:"vertical"}}
            />

            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:22,flexWrap:"wrap",gap:12}}>
              <p style={{margin:0,fontSize:11,color:"#6B7280"}}>By submitting you agree to our verification process — we may ask for sample work and GST / registration proofs before onboarding.</p>
              <button
                type="submit"
                data-testid="partner-submit"
                disabled={busy || form.specialties.length === 0}
                style={{
                  background: (busy || form.specialties.length === 0) ? "#9CA3AF" : "#111827",
                  color: "#FACC15",
                  border: "none",
                  padding: "11px 24px",
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: (busy || form.specialties.length === 0) ? "not-allowed" : "pointer",
                }}
              >{busy ? "Submitting…" : "Submit application"}</button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}

function PartnerField({ label, full, ...rest }) {
  return (
    <label style={{display:"flex",flexDirection:"column",gap:4,fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"0.05em",textTransform:"uppercase", gridColumn: full ? "1 / -1" : undefined, marginTop: full ? 14 : 0}}>
      {label}
      <input {...rest} style={{padding:"9px 11px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13,color:"#111827",fontWeight:500,textTransform:"none",letterSpacing:"normal"}}/>
    </label>
  );
}
